import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { listTrays } from '@/js/core/utils/trays-api';
import { FILTER_API_MAP, TRAYS_PER_PAGE } from '@/js/core/constants/tray-constants';

/**
 * Store for managing the trays list
 * Provides caching, filtering, and pagination
 */
export const useTraysListStore = defineStore('traysList', () => {
  const trays = ref([]);
  const meta = ref(null);
  const isLoading = ref(false);
  const error = ref('');
  const filter = ref([]);
  const lastFetchTime = ref(null);
  const lastFetchFilters = ref([]);
  let fetchId = 0;

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  const activeApiFilters = computed(() => {
    return filter.value.map(f => FILTER_API_MAP[f]).filter(Boolean);
  });

  const hasMore = computed(() => {
    if (!meta.value) return false;
    return meta.value.current_page < meta.value.last_page;
  });

  const isCacheValid = computed(() => {
    if (!lastFetchTime.value) return false;
    return Date.now() - lastFetchTime.value < CACHE_DURATION;
  });

  // Mark the cached list stale so the next fetchTrays() refetches from the server.
  function invalidate() {
    lastFetchTime.value = null;
  }

  async function fetchTrays({ page = 1, perPage = TRAYS_PER_PAGE, force = false } = {}) {
    const filtersChanged =
      JSON.stringify(activeApiFilters.value) !== JSON.stringify(lastFetchFilters.value);

    // Return cached data if valid and not forced
    if (!force && page === 1 && isCacheValid.value && trays.value.length > 0 && !filtersChanged) {
      return { data: trays.value, meta: meta.value };
    }

    isLoading.value = true;
    error.value = '';
    const currentFetchId = ++fetchId;

    try {
      const response = await listTrays({
        page,
        perPage,
        filters: activeApiFilters.value,
      });

      // Discard stale responses from rapid filter changes
      if (currentFetchId !== fetchId) return;

      if (page === 1) {
        trays.value = response.data || [];
      } else {
        // Append for pagination
        trays.value = [...trays.value, ...(response.data || [])];
      }

      meta.value = response.meta?.pagination || null;
      lastFetchTime.value = Date.now();
      lastFetchFilters.value = [...activeApiFilters.value];

      return response;
    } catch (err) {
      if (currentFetchId !== fetchId) return;
      error.value = err.message || 'Failed to load trays.';
      throw err;
    } finally {
      if (currentFetchId === fetchId) {
        isLoading.value = false;
      }
    }
  }

  async function loadMore() {
    if (!hasMore.value || isLoading.value) return;

    const nextPage = (meta.value?.current_page || 0) + 1;
    await fetchTrays({ page: nextPage, perPage: meta.value?.per_page || TRAYS_PER_PAGE });
  }

  function setFilter(value) {
    filter.value = value;
    trays.value = [];
    meta.value = null;
    lastFetchTime.value = null;
    fetchTrays({ perPage: TRAYS_PER_PAGE, force: true }).catch(() => {});
  }

  function reset() {
    trays.value = [];
    meta.value = null;
    error.value = '';
    filter.value = [];
    lastFetchTime.value = null;
    lastFetchFilters.value = [];
  }

  return {
    trays,
    meta,
    isLoading,
    error,
    filter,
    hasMore,
    fetchTrays,
    loadMore,
    invalidate,
    setFilter,
    reset,
  };
});
